import React, { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import styles from "./style.module.css";
import altLogo from "../../assets/images/alt-logo.svg";
import altBankLogo from "../../assets/images/alt-finance-logo.svg";
import { useFormik } from "formik";
import * as Yup from "yup";
import closeIcon from "../../assets/images/close-icon.svg";
import uploadArrow from "../../assets/images/upload-arrow.svg";
import {
  MAX_FILE_UPLOAD_SIZE_FIGURE,
  MAX_FILE_UPLOAD_SIZE_TEXT,
} from "../../utils/constants";
import * as toast from "../../utils/makeToast";
import axios from "axios";
import { baseUrl } from "../../utils/baseUrl";
import pen from "../../assets/images/pen.svg";
import { trimImageURI } from "../../utils/imageUpload";
import SignatureCanvas from "react-signature-canvas";
import Loader from "../../Components/Loader";
import DOMPurify from "dompurify";
import { encryptRequest } from "../../utils/encryption";

function ReferenceConsent(props: any) {
  const navigate = useNavigate();

  const { token } = useParams();

  const [consenting, setConsenting] = useState(false);

  const [signatureImageUrl, setSignatureImageUrl] = useState("");
  const [settingSignatureImage, setSettingSignatureImage] =
    useState<boolean>(false);

  const [showSignaturePad, setShowSignaturePad] = useState<boolean>(false);
  const [signatureName, setSignatureName] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      refereeAccountNumber: "",
      refereeAccountName: "",
      nameOfAccountHolder: "",
      lengthOfRelationship: "",
    },
    validationSchema: Yup.object({
      refereeAccountNumber: Yup.string().required(
        "Referee account number is required"
      ),
      refereeAccountName: Yup.string().required(
        "Referee account name is required"
      ),
      lengthOfRelationship: Yup.string().required(
        "Length of relationship is required"
      ),
    }),
    onSubmit: (values) => {
      submitConsent(values);
    },
  });

  const submitConsent = (values: any) => {
    setConsenting(true);
    let refereeConsentPayload = {
      refereeAccountNumber: values.refereeAccountNumber,
      refereeAccountName: values.refereeAccountName,
      nameOfAccountHolder: values.nameOfAccountHolder,
      lengthOfRelationship: `${values.lengthOfRelationship}`,
      signature: signatureImageUrl,
      referenceId: token,
    };

    // return
    axios
      .post(`${baseUrl}Referee/refereeconsent`, refereeConsentPayload)
      .then((response) => {
        setConsenting(false);
        if (!response?.data?.status) {
          toast.errorToast(`${response?.data?.responseMessage}`);
          return;
        }
        toast.successToast("Successful");
        navigate("/");
      })
      .catch((err) => {
        setConsenting(false);
        toast.errorToast(
          err?.response?.data?.title
            ? err?.response?.data?.title
            : err?.response?.data?.message
        );
      });
  };

  let fileUploadObj: any;

  const uploadImage = (payload: any, type: string) => {
    if (type === "signature") {
      setSettingSignatureImage(true);
    }
    axios
      .post(`${baseUrl}Authentication/UploadFile`, payload)
      .then((response) => {
        if (type == "signature") {
          setSignatureImageUrl(response.data?.id);
          setSettingSignatureImage(false);
          if (response.data?.id) {
            toast.successToast(response.data?.message);
          }
        }
      })
      .catch((err) => {
        setSettingSignatureImage(false);
        toast.errorToast(
          err?.response?.data?.title
            ? err?.response?.data?.title
            : err?.response?.data?.message
        );
      });
  };

  const handleSubmit = (type: string) => {
    uploadImage(fileUploadObj, type);
  };

  const convertToBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        let fileReaderResult = fileReader.result?.toString();
        if (
          fileReader.result
            ?.toString()
            .substring(0, 25)
            .split("/")[1]
            .split(";")[0] === "png" ||
          fileReader.result
            ?.toString()
            .substring(0, 25)
            .split("/")[1]
            .split(";")[0] === "jpg" ||
          fileReader.result
            ?.toString()
            .substring(0, 25)
            .split("/")[1]
            .split(";")[0] === "pdf"
        ) {
          return resolve(fileReaderResult?.substring(22));
        } else if (
          fileReader.result
            ?.toString()
            .substring(0, 25)
            .split("/")[1]
            .split(";")[0] === "jpeg"
        ) {
          return resolve(fileReaderResult?.substring(23));
        } else {
          toast.errorToast(`Invalid file format`);
        }
        // resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleFileUpload = async (e: any, type: string) => {
    const file = e.target.files[0];
    let fileSize = file.size / 1000;
    const allowedFileTypes = ["image/jpeg", "image/png", "application/pdf"];

    // Validate file type
    if (!allowedFileTypes.includes(file.type)) {

      toast.errorToast("Only image (JPEG, PNG) or PDF files are allowed.");
      return;
    }
    if (fileSize > MAX_FILE_UPLOAD_SIZE_FIGURE) {
      toast.errorToast(
        `File size should be less than ${MAX_FILE_UPLOAD_SIZE_TEXT}`
      );
      return;
    }
    let base64: any;

    convertToBase64(file).then((res) => {
      base64 = res;
      fileUploadObj = {
        postedFilebase64: res,
        documentName: type,
        filetype: file.type.split("/")[1],
      };
      handleSubmit(type);
    });
  };

  const clearImage = (type: any) => {
    if (type === "signature") {
      setSignatureImageUrl("");
    }
  };

  let sigPad: any = {};

  const toggleSignaturePad = (type: string, value: boolean) => {
    setSignatureName(type);
    setShowSignaturePad(value);
  };

  const closeSignaturePad = () => {
    clearSignaturePad();
    setShowSignaturePad(false);
  };

  const clearSignaturePad = () => {
    sigPad.clear();
  };

  const uploadSignaturePadSketch = () => {
    let res = trimImageURI(sigPad.getTrimmedCanvas().toDataURL("image/png"));
    fileUploadObj = {
      postedFilebase64: res,
      documentName: signatureName,
      filetype: "png",
    };
    handleSubmit(signatureName);
    closeSignaturePad();
  };

  const getReferee = (token) => {
    setLoading(true);
    axios
      .get(`${baseUrl}Referee/getreferee?referenceId=${encryptRequest(token)}`)
      .then((response) => {
        let refereeDetails = response?.data?.data;

        formik.setValues({
          refereeAccountName: refereeDetails?.refereeAccountName,
          lengthOfRelationship: ``,
          nameOfAccountHolder: refereeDetails?.customerName,
          refereeAccountNumber: refereeDetails?.refereeAccountNumber,
        });
        setLoading(false);
        if (!response?.data?.status) {
          toast.errorToast(`${response?.data?.responseMessage}`);
          return;
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.errorToast("Error fetching referee details");
      });
  };

  const dontConsent = () => {
    navigate("/");
  };

  useEffect(() => {
    getReferee(token);
  }, []);

  return (
    <div>
      {loading ? <Loader /> : null}

      {/* <div>
        <Toaster />
      </div> */}

      {showSignaturePad && (
        <div className="pt-4 pb-4 signature-pad-div">
          <p className="text-center">Draw your signature inside the box</p>
          <div className="signature-pad-inner-div">
            <SignatureCanvas
              penColor="black"
              ref={(ref) => {
                sigPad = ref;
              }}
              canvasProps={{ width: 300, height: 200, className: "sigCanvas" }}
            />
          </div>
          <div className="d-flex justify-content-center mt-4">
            <button
              className="signature-cancel-btn"
              onClick={closeSignaturePad}
            >
              Close
            </button>
            <button className="signature-clear-btn" onClick={clearSignaturePad}>
              Clear
            </button>
            <button
              className="signature-upload-btn"
              onClick={uploadSignaturePadSketch}
            >
              Upload
            </button>
          </div>
        </div>
      )}

      <form onSubmit={formik.handleSubmit}>
        <div className="container-fluid row">
          <div
            className="col-md-5 d-none d-md-inline left_col"
            style={{ minHeight: "100vh" }}
          >
            <div className="logo pl-4 pt-5">
              <img src={altBankLogo} alt="alternative finance logo" />
            </div>
          </div>
          <div className="col-md-7 right_col">
            <div
              className="d-flex justify-content-end"
              style={{ visibility: "hidden" }}
            >
              <div className="d-flex flex-column pt-5">
                <div className="d-flex justify-content-end">
                  <h1 className="pr-3 mb-4 text-capitalize">account type</h1>
                </div>
                nav
              </div>
            </div>
            <div className="row">
              <div className="col-md-11 card validation-card pb-4 d-non">
                <div className={`card-form ${styles.bvn_card}`}>
                  <div className="card-body">
                    <h4 className="card-title text-center pl-5 mt-4">
                      Confirm Reference
                    </h4>
                    <div className="mb-4 text-center mt-4">
                      <small className="ml-4">
                        The file format should be JPEG or PDF
                      </small>
                    </div>

                    <div className="row mb-4">
                      <div className="col-sm-6 col-12">
                        <div className="form-group">
                          <div className="d-flex justify-content-between pb-1 fillup">
                            <label htmlFor="name" className="fila">
                              Referee's account number
                            </label>
                          </div>
                          <input
                            name="refereeAccountNumber"
                            onChange={formik.handleChange}
                            value={formik.values.refereeAccountNumber}
                            placeholder="0123456789"
                            className="form-control bvn_input border-dark"
                            style={{ textAlign: "left" }}
                            type="text"
                            id="bvn"
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-sm-6 col-12">
                        <div className="form-group">
                          <div className="d-flex justify-content-between pb-1 fillup">
                            <label htmlFor="name" className="fila">
                              Referee's account name
                            </label>
                          </div>
                          <input
                            name="refereeAccountName"
                            onChange={formik.handleChange}
                            value={formik.values.refereeAccountName}
                            placeholder="John Doe"
                            className="form-control bvn_input border-dark"
                            style={{ textAlign: "left" }}
                            type="text"
                            id="bvn"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row mb-4">
                      <div className="col-12">
                        <div className="form-group">
                          <div className="d-flex justify-content-between pb-1 fillup">
                            <label htmlFor="name" className="fila">
                              Name of account holder
                            </label>
                          </div>
                          <input
                            name="nameOfAccountHolder"
                            onChange={formik.handleChange}
                            value={formik.values.nameOfAccountHolder}
                            placeholder="Musa Chukwu"
                            className="form-control bvn_input border-dark"
                            style={{ textAlign: "left" }}
                            type="text"
                            id="bvn"
                            disabled
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-6">
                        <div className="form-group">
                          <div className="d-flex justify-content-between pb-1 fillup">
                            <label htmlFor="name" className="fila">
                              Length of relationship with the account holder in
                              years
                            </label>
                          </div>
                          <input
                            type="number"
                            name="lengthOfRelationship"
                            onChange={formik.handleChange}
                            value={formik.values.lengthOfRelationship}
                            placeholder="10"
                            className="form-control bvn_input border-dark"
                            style={{ textAlign: "left" }}
                            id="bvn"
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        {!signatureImageUrl ? (
                          <React.Fragment>
                            <div className="col-sm-12 row">
                              <div className="col-sm-6">
                                <div className="col-md-6">
                                  <label
                                    htmlFor="firstname"
                                    className="label_text2 d-inline text-nowrap"
                                  >
                                    Draw/UploadSignature
                                  </label>
                                  <br />
                                  <label
                                    className="cursor-pointer"
                                    htmlFor="inputTagSignature"
                                  >
                                    <div className="pic_upload d-flex justify-content-between pt-3 pr-3 px-2">
                                      {settingSignatureImage ? (
                                        <div className="center-v">
                                          <div
                                            className="spinner-border text-gold"
                                            role="status"
                                          >
                                            <span className="sr-only"></span>
                                          </div>
                                        </div>
                                      ) : (
                                        <React.Fragment>
                                          <p className="label_text">
                                            Click or drag <br /> and drop file
                                          </p>
                                          <img
                                            className="img-fluid"
                                            src={uploadArrow}
                                            alt=""
                                          />
                                        </React.Fragment>
                                      )}
                                    </div>
                                    <input
                                      onChange={(e) =>
                                        handleFileUpload(e, "signature")
                                      }
                                      id="inputTagSignature"
                                      className="input-file-type"
                                      type="file"
                                      name="file"
                                      accept="image/png, image/jpg, image/jpeg"
                                    />
                                  </label>
                                </div>
                              </div>
                              <div className="col-sm-6">
                                <div className="col-md-6 mt-4">
                                  <div
                                    className="pic_upload d-flex justify-content-between pt-3 pr-3 px-2"
                                    onClick={() =>
                                      toggleSignaturePad("signature", true)
                                    }
                                  >
                                    <p className="label_text">
                                      Draw an <br /> e-signature
                                    </p>
                                    <img
                                      className="img-fluid"
                                      src={pen}
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        ) : (
                          ""
                        )}

                        {signatureImageUrl ? (
                          <div className="col-md-6">
                            <div className="img-upload-success-div">
                              <div className="close-icon-signature-div">
                                <div className="close-icon-img cursor-pointer">
                                  <img
                                    src={closeIcon}
                                    onClick={() => clearImage("image")}
                                  ></img>
                                </div>
                                <div className="signature-img-div">
                                  <img
                                    src={DOMPurify.sanitize(signatureImageUrl)}
                                    width="70%"
                                    height="120px"
                                  ></img>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          ""
                        )}

                        {/* <div className="form-group">
                                                    <div className="d-flex justify-content-between pb-1 fillup">
                                                        <label htmlFor="name" className="fila">Upload Signature</label>
                                                    </div>
                                                    <input
                                                        name="bvn"
                                                        className="form-control bvn_input border-dark" style={{ textAlign: 'left' }} type="text" id="bvn" placeholder="Enter your BVN" />
                                                </div> */}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                      <div
                        className="mb-3 mr-4"
                        style={{ marginRight: "20px" }}
                      >
                        <button
                          onClick={dontConsent}
                          className="btn btn-outline-gold mr-3 btn-filled-white"
                        >
                          I do not consent
                        </button>
                      </div>
                      {consenting ? (
                        <div className="spinner-border text-gold" role="status">
                          <span className="sr-only"></span>
                        </div>
                      ) : (
                        <button
                          type="submit"
                          className="btn btn-dange float-right btn-filled-red"
                        >
                          I consent
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ReferenceConsent;
